import {
  Paper,
  Title,
  Text,
  TextInput,
  PasswordInput,
  Button,
  Flex,
  Stepper,
} from "@mantine/core";
import { IconBuilding, IconMail, IconLock } from "@tabler/icons-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import VerifyEmailModal from "../signup/VerifyModal";
import trpc from "../../shared/utils/trpc";
import { handleTrpcError } from "../../shared/utils/trpc/handletrpcError.util";
import { successNotification } from "../../shared/utils/successNotification";
import { SignupFlowLayout } from "./SignupFlowLayout";

function SignupPage() {
  const navigate = useNavigate();

  const [emailModalOpened, setEmailModalOpened] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    companyName: "",
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isPasswordValid = formData.password.length >= 8;
  const doPasswordsMatch = formData.confirmPassword === "" || formData.password === formData.confirmPassword;

  const isStep1Valid =
    formData.name.trim() !== "" &&
    formData.companyName.trim() !== "" &&
    isValidEmail(formData.email) &&
    isPasswordValid &&
    formData.password === formData.confirmPassword;

  const handleStep1Submit = async () => {
    if (!isStep1Valid) return;

    setLoading(true);
    try {
      await trpc.auth.registerSignupStep1.mutate({
        email: formData.email,
        password: formData.password,
        userName: formData.name,
        companyName: formData.companyName,
      });

      successNotification({
        title: "Check your email",
        message: "We sent a verification link. After verifying, you can complete your company profile.",
      });

      navigate(`/resend-email?email=${encodeURIComponent(formData.email)}`);
      setEmailModalOpened(true);
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleModalContinue = () => {
    setEmailModalOpened(false);
    navigate("/registration-success", {
      state: {
        companyName: formData.companyName,
        location: "—",
        email: formData.email,
      },
    });
  };

  return (
    <SignupFlowLayout>
      <Paper p="xl" radius="lg" maw={900} mx="auto" style={{ border: "1px solid #E5E7EB" }}>
        <Stepper active={0} mb="xl">
          <Stepper.Step label="Account" description="Verify email" />
          <Stepper.Step label="Company details" />
          <Stepper.Step label="Documents" />
        </Stepper>

        <Title order={4} mb="md" c="slate.7" style={{ borderBottom: "1px solid #eee", paddingBottom: "8px" }}>
          Create your account
        </Title>
        <Text fz="sm" c="slate.6" mb="lg">
          Personal Details
        </Text>

        <TextInput
          label="Contact Person Name"
          placeholder="Enter name"
          required
          mb="md"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.currentTarget.value })}
        />

        <TextInput
          label="Company Name"
          placeholder="Enter your company name"
          required
          mb="md"
          value={formData.companyName}
          onChange={(e) => setFormData({ ...formData, companyName: e.currentTarget.value })}
          leftSection={<IconBuilding size={18} />}
        />

        <TextInput
          label="Company Email Address"
          required
          type="email"
          placeholder="Enter email"
          mb="md"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.currentTarget.value })}
          leftSection={<IconMail size={18} />}
          error={formData.email && !isValidEmail(formData.email) ? "Please enter a valid email address" : null}
        />

        <Flex direction={{ base: "column", sm: "row" }} gap="md" mb="xl">
          <PasswordInput
            style={{ flex: 1 }}
            label="Password"
            placeholder="Enter password"
            required
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.currentTarget.value })}
            visible={showPassword}
            onVisibilityChange={setShowPassword}
            leftSection={<IconLock size={18} />}
            error={
              formData.password.length > 0 && !isPasswordValid ? "Password must be at least 8 characters" : null
            }
          />

          <PasswordInput
            style={{ flex: 1 }}
            label="Confirm Password"
            placeholder="Re-enter password"
            required
            value={formData.confirmPassword}
            onChange={(e) => setFormData({ ...formData, confirmPassword: e.currentTarget.value })}
            visible={showConfirmPassword}
            onVisibilityChange={setShowConfirmPassword}
            leftSection={<IconLock size={18} />}
            error={formData.confirmPassword && !doPasswordsMatch ? "Passwords don't match" : null}
          />
        </Flex>

        <Flex justify="flex-end">
          <Button size="md" px="xl" onClick={handleStep1Submit} loading={loading} disabled={!isStep1Valid || loading}>
            Continue
          </Button>
        </Flex>
      </Paper>

      <VerifyEmailModal
        opened={emailModalOpened}
        onClose={() => setEmailModalOpened(false)}
        email={formData.email}
        onContinue={handleModalContinue}
      />
    </SignupFlowLayout>
  );
}

export default SignupPage;
